import {createEvent, createStore, guard, sample} from '@effector/effector'
import {MAX_IN_FLIGHT_ITEMS} from '../env'
import {play, pause, isPlay} from './playState'
import {runRuntime} from '../workingSet'
import {runtimeID} from './runtimeID'

export const addPendingEffect = createEvent<{
  name: string
  callID: number
  params: any
  runtimeID: string
}>()
export const removePendingEffect = createEvent<{
  callID: number
  status: 'done' | 'fail'
  value: any
  runtimeID: string
  cb: (data: any) => void
}>()

const pausedSince = createStore(-1)
  .reset(play)
  .on(pause, () => performance.now())
const removePendingEffectOnPlay = guard(removePendingEffect, {
  filter: isPlay,
  //@ts-ignore
  greedy: true
})
const removePendingEffectOnPauseRaw = guard(removePendingEffect, {
  filter: isPlay.map(isPlay => !isPlay),
  //@ts-ignore
  greedy: true
})

type SuspendedEffect = {
  offset: number
  data: {
    callID: number
    status: 'done' | 'fail'
    value: any
    runtimeID: string
    cb: (data: any) => void
  }
}
type SuspendedTimer = {
  fx: SuspendedEffect
  timer: NodeJS.Timeout
}
const removePendingEffectOnPause = sample({
  source: pausedSince,
  clock: removePendingEffectOnPauseRaw,
  greedy: true,
  fn: (since, data) => ({
    offset: performance.now() - since,
    data
  })
})
const removeSuspendedEffect = createEvent<SuspendedEffect>()

const suspendedEffects = createStore<SuspendedEffect[]>([])
  .on(removePendingEffectOnPause, (list, fx: SuspendedEffect) =>
    [...list, fx].slice(-100)
  )
  .on(removeSuspendedEffect, (list, {data: {callID}}) =>
    list.filter(item => item.data.callID !== callID)
  )
const suspendedTimers = createStore<SuspendedTimer[]>([])
  .on(pause, list => {
    if (list.length === 0) return
    for (let i = 0; i < list.length; i++) {
      clearTimeout(list[i].timer)
    }
    return []
  })
  .on(runtimeID, (list, runtimeID) =>
    list.filter(item => {
      if (item.fx.data.runtimeID === runtimeID) return true
      clearTimeout(item.timer)
      return false
    })
  )
sample({
  source: suspendedEffects,
  clock: play,
  fn(list) {
    const minimalTimeout = Math.min(...list.map(({offset}) => offset))
    return list.map((fx: SuspendedEffect) => ({
      fx,
      timer: setTimeout(removeSuspendedEffect, fx.offset - minimalTimeout, fx)
    }))
  },
  target: suspendedTimers
})

removePendingEffectOnPlay.watch(({value, cb}) => {
  cb(value)
})
removeSuspendedEffect.watch(({data: {value, cb}}) => {
  cb(value)
})
//TODO add offset to effects
//which added before pause AND resolved after play
export const pendingEffects = createStore<
  Array<{
    name: string
    callID: number
    params: any
    runtimeID: string
  }>
>([])
  .on(addPendingEffect, (list, item) =>
    [...list, item].slice(-MAX_IN_FLIGHT_ITEMS)
  )
  .on(removePendingEffectOnPlay, (list, {callID}) =>
    list.filter(item => item.callID !== callID)
  )
  .on(removeSuspendedEffect, (list, {data: {callID}}) =>
    list.filter(item => item.callID !== callID)
  )
  .reset(runRuntime)
